import React, { useState, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import Modal from '../Modal';
import OtpInput from '../OtpInput';
import Button from '../Button';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { sendPinForPiiVerification } from 'modules/state-mgmt/onboarding';
import { prettifyErrorResponse } from 'utils';

import { actionMapForOtherLoaders } from 'constants/actions';
import { useStyles } from './styles';
import { fetchWorkerDetails, verifyPiiPin } from 'modules/state-mgmt/onboarding/actions';

interface IOtpVerificationModalsProps {
  onClose: () => void;
}

const OtpVerificationModals = ({ onClose }: IOtpVerificationModalsProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [isInformationModalOpen, setIsInformationModalOpen] = useState(true);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const [refresher, setRefresher] = useState(true);
  const {
    email,
    mobilePhoneNumber,
    workerId,
    languageCode,
    sendPinForPiiVerificationLoading,
    verifyPiiPinLoading,
    fetchWorkerDetailsLoading,
  } = useAppSelector(state => ({
    email: state.onboarding.email,
    mobilePhoneNumber: state.onboarding.mobilePhoneNumber,
    workerId: state.onboarding.id,
    languageCode: state.app.languageCode,
    sendPinForPiiVerificationLoading: state.loader.otherLoaders[actionMapForOtherLoaders.sendPinForPiiVerification],
    verifyPiiPinLoading: state.loader.otherLoaders[actionMapForOtherLoaders.verifyPiiPin],
    fetchWorkerDetailsLoading: state.loader.otherLoaders[actionMapForOtherLoaders.fetchWorkerDetails],
  }));
  const dispatch = useAppDispatch();
  const isVerifyLoading = verifyPiiPinLoading?.isLoading || fetchWorkerDetailsLoading?.isLoading;

  const onShowVerificationModal = () => {
    setIsInformationModalOpen(false);
    setTimeout(() => {
      setIsVerificationModalOpen(true);
    }, 100);
  };

  const getContactMethodText = () => {
    if (mobilePhoneNumber && email) {
      return `${email} ${t('common.orSmall')} ${mobilePhoneNumber}`;
    }
    return mobilePhoneNumber || email || '';
  };

  const sendPin = async () => {
    try {
      await dispatch(sendPinForPiiVerification({ workerId, languageCode })).unwrap();
    } catch (error) {
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    }
  };

  const onVerify = async () => {
    try {
      await dispatch(verifyPiiPin({ workerId, languageCode, pinCode: otp })).unwrap();
      await dispatch(fetchWorkerDetails({ workerId, languageCode })).unwrap();
      enqueueSnackbar('Pin verification successful!', { variant: 'success' }); //! Remove this message once the whole functionality is implemented
      onClose();
    } catch (error) {
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    }
  };

  useEffect(() => {
    if (isVerificationModalOpen) {
      sendPin();
    }
  }, [isVerificationModalOpen, refresher]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && otp) {
      onVerify();
    }
  };

  return (
    <>
      {isInformationModalOpen && (
        <Modal
          title={t('piiPinVerificationModals.title')}
          modalStyleClass={classes.modal}
          Content={
            <>
              <p className={classes.infoText}>{t('piiPinVerificationModals.infoText')}</p>
              <p className={classes.receivePinText}>{t('piiPinVerificationModals.receivePin')}</p>
            </>
          }
          actionButtons={[
            {
              label: t('common.yes'),
              styleClass: classes.yesButton,
              onClick: onShowVerificationModal,
              testId: 'pii-verification-yes-button',
            },
          ]}
          onClose={onClose}
        />
      )}
      {isVerificationModalOpen && (
        <Modal
          title={t('piiPinVerificationModals.title')}
          modalStyleClass={classes.modal}
          Content={
            <>
              <p className={classes.infoText}>
                {t('piiPinVerificationModals.enterPin')}{' '}
                <span className={classes.contactMethodText}>{getContactMethodText()}</span>
              </p>
              <div onKeyDown={handleKeyDown}>
                <OtpInput
                  allowCharacters={true}
                  length={5}
                  styleClass={classes.otpInput}
                  onComplete={otp => setOtp(otp)}
                  onResetOtp={() => setOtp('')}
                  autoFocus
                />
              </div>
              <div className={classes.resentPinParent}>
                <Button
                  styleClass={classes.resendPinButton}
                  disabled={sendPinForPiiVerificationLoading?.isLoading}
                  variant={'text'}
                  disableRipple={true}
                  label={t('piiPinVerificationModals.resendPin')}
                  testId="pii-verification-resend-button"
                  onClick={() => setRefresher(prev => !prev)}
                />
              </div>
            </>
          }
          actionButtons={[
            {
              label: t('common.verify'),
              disabled: !otp,
              styleClass: classes.verifyButton,
              isLoading: isVerifyLoading,
              onClick: onVerify,
              testId: 'pii-verification-verify-button',
            },
          ]}
          onClose={onClose}
        />
      )}
    </>
  );
};

export default memo(OtpVerificationModals);
